import React, { useEffect } from "react";
import { Box, Flex, SimpleGrid, Text, Heading, Icon, VStack, HStack } from "@chakra-ui/react";
import {
  FiLock, FiSearch, FiCheckCircle, FiMail, FiUser, FiShield,
  FiGlobe, FiSmartphone, FiKey, FiServer, FiArrowRight
} from "react-icons/fi";
import { InfoPage, InfoBody, InfoSectionTitle, FaqList } from "../infoShared";

const PRIMARY = "#430098";
const ACCENT = "#e51f44";

/* ─── Diagram: co blockchain gwarantuje zawsze ─────────────────────────── */
const GUARANTEES = [
  { icon: FiLock, title: "Niezaprzeczalność", desc: "Raz oddany głos jest publicznie zapisany na łańcuchu — nie da się go po cichu zmienić ani usunąć." },
  { icon: FiSearch, title: "Audytowalność", desc: "Każdy może samodzielnie policzyć głosy na łańcuchu i sprawdzić, że ogłoszony wynik się zgadza." },
  { icon: FiCheckCircle, title: "Jeden klucz = jeden głos", desc: "Ten sam wyborca nie odda dwóch głosów w tym samym głosowaniu — łańcuch tego pilnuje." }
];

const GuaranteeCards: React.FC = () => (
  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: "16px", md: "20px" }}>
    {GUARANTEES.map(g => (
      <VStack key={g.title} align="flex-start" spacing="12px" bg="white" borderRadius="20px" p={{ base: "22px", md: "26px" }}
        border="1px solid" borderColor="rgba(67,0,152,0.08)" boxShadow="0 8px 24px rgba(67,0,152,0.06)">
        <Flex w="48px" h="48px" borderRadius="14px" align="center" justify="center"
          bgGradient={`linear(135deg, ${PRIMARY}, #6a1fc2)`} color="white">
          <Icon as={g.icon} boxSize="22px" />
        </Flex>
        <Heading fontSize="18px" fontWeight="800" color="primary">{g.title}</Heading>
        <Text fontSize="15px" color="gray.600" lineHeight="1.6">{g.desc}</Text>
      </VStack>
    ))}
  </SimpleGrid>
);

/* ─── Diagram: drabina tożsamości wyborcy ──────────────────────────────── */
const TIERS = [
  { icon: FiMail, level: "Próg 1", title: "Bez konta", desc: "Wystarczy zaproszenie (token/e-mail). Najniższy próg — np. zarząd, radni.", w: "62%" },
  { icon: FiUser, level: "Próg 2", title: "Zalogowane konto", desc: "Głosować mogą tylko zalogowani. Mocniejsza tożsamość i historia udziału.", w: "80%" },
  { icon: FiShield, level: "Próg 3", title: "Zweryfikowana tożsamość", desc: "Konto + potwierdzona tożsamość (mObywatel). Odporność na fałszywe konta.", w: "100%" }
];

const EligibilityLadder: React.FC = () => (
  <VStack align="stretch" spacing="14px">
    {TIERS.map((t, i) => (
      <Flex key={t.title} align="center" gap={{ base: "14px", md: "20px" }}>
        <Box position="relative" flex="1" minW={0}>
          <Flex align="center" gap="14px" borderRadius="18px" p={{ base: "16px", md: "20px" }} color="white"
            w={{ base: "100%", md: t.w }} bgGradient={`linear(135deg, ${PRIMARY} 0%, #7a28cc ${40 + i * 20}%, ${ACCENT} 130%)`}
            boxShadow="0 10px 26px rgba(67,0,152,0.18)">
            <Flex w="44px" h="44px" borderRadius="full" bg="rgba(255,255,255,0.18)" align="center" justify="center" flexShrink={0}>
              <Icon as={t.icon} boxSize="20px" />
            </Flex>
            <Box>
              <Text fontSize="11px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" opacity={0.8}>{t.level}</Text>
              <Heading fontSize={{ base: "16px", md: "18px" }} fontWeight="800">{t.title}</Heading>
              <Text fontSize={{ base: "13px", md: "14px" }} opacity={0.92} lineHeight="1.5" mt="2px">{t.desc}</Text>
            </Box>
          </Flex>
        </Box>
      </Flex>
    ))}
    <Text fontSize="14px" color="gray.500" fontStyle="italic" mt="4px">
      Im wyżej, tym mocniejsza pewność, kto naprawdę głosuje. Organizator wybiera próg pod rangę głosowania.
    </Text>
  </VStack>
);

/* ─── Diagram: web vs urządzenie z portfelem ───────────────────────────── */
const WebVsWallet: React.FC = () => (
  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: "18px", md: "22px" }}>
    {/* WEB */}
    <VStack align="flex-start" spacing="14px" bg="white" borderRadius="22px" p={{ base: "24px", md: "28px" }}
      border="1px solid" borderColor="rgba(67,0,152,0.1)" boxShadow="0 8px 24px rgba(67,0,152,0.06)">
      <HStack spacing="12px">
        <Flex w="46px" h="46px" borderRadius="14px" bg="rgba(67,0,152,0.08)" color="primary" align="center" justify="center">
          <Icon as={FiGlobe} boxSize="22px" />
        </Flex>
        <Box>
          <Heading fontSize="19px" fontWeight="800" color="primary">Przeglądarka (web)</Heading>
          <Text fontSize="13px" color="gray.500">Codzienne, wygodne głosowanie</Text>
        </Box>
      </HStack>
      <VStack align="stretch" spacing="9px" w="100%">
        <CapRow ok label="Token / e-mail / sesja konta" />
        <CapRow ok label="Potwierdzenie głosu mailem lub w sesji" />
        <CapRow label="Podpis kluczem tożsamości (portfel)" note="brak klucza w przeglądarce" />
      </VStack>
    </VStack>

    {/* URZĄDZENIE Z PORTFELEM */}
    <VStack align="flex-start" spacing="14px" borderRadius="22px" p={{ base: "24px", md: "28px" }} color="white"
      bgGradient={`linear(150deg, ${PRIMARY} 0%, #6a1fc2 55%, #8b2fd6 100%)`} boxShadow="0 14px 34px rgba(67,0,152,0.22)">
      <HStack spacing="12px">
        <Flex w="46px" h="46px" borderRadius="14px" bg="rgba(255,255,255,0.16)" align="center" justify="center" position="relative">
          <Icon as={FiSmartphone} boxSize="22px" />
          <Flex position="absolute" bottom="-6px" right="-6px" w="22px" h="22px" borderRadius="full" bg={ACCENT} align="center" justify="center">
            <Icon as={FiKey} boxSize="11px" />
          </Flex>
        </Flex>
        <Box>
          <Heading fontSize="19px" fontWeight="800">Aplikacja z portfelem</Heading>
          <Text fontSize="13px" color="rgba(255,255,255,0.8)">Najwyższy poziom — suwerenny głos</Text>
        </Box>
      </HStack>
      <VStack align="stretch" spacing="9px" w="100%">
        <CapRow ok onDark label="Wszystko to, co web" />
        <CapRow ok onDark label="Klucz tożsamości @PL-U- w bezpiecznym module urządzenia" />
        <CapRow ok onDark label="Podpis głosu Twoim własnym kluczem — nikt go nie podrobi" />
      </VStack>
    </VStack>
  </SimpleGrid>
);

const CapRow: React.FC<{ label: string; ok?: boolean; onDark?: boolean; note?: string }> = ({ label, ok, onDark, note }) => (
  <Flex align="flex-start" gap="10px">
    <Flex w="20px" h="20px" borderRadius="full" flexShrink={0} mt="2px" align="center" justify="center"
      bg={ok ? (onDark ? "rgba(255,255,255,0.22)" : "rgba(67,0,152,0.1)") : "rgba(229,31,68,0.12)"}>
      <Text fontSize="12px" fontWeight="900" color={ok ? (onDark ? "white" : "primary") : ACCENT} lineHeight="1">{ok ? "✓" : "—"}</Text>
    </Flex>
    <Box>
      <Text fontSize="14px" fontWeight="600" color={onDark ? "white" : "gray.700"} lineHeight="1.4">{label}</Text>
      {note && <Text fontSize="12px" color={onDark ? "rgba(255,255,255,0.7)" : "gray.400"}>{note}</Text>}
    </Box>
  </Flex>
);

/* ─── Diagram: dwa modele zaufania (standardowy vs suwerenny) ───────────── */
const ModelFlow: React.FC<{ steps: { icon: React.ElementType; label: string }[]; dark?: boolean }> = ({ steps, dark }) => (
  <Flex align="center" wrap="wrap" gap="8px">
    {steps.map((s, i) => (
      <React.Fragment key={s.label}>
        <VStack spacing="6px" minW="84px">
          <Flex w="44px" h="44px" borderRadius="13px" align="center" justify="center"
            bg={dark ? "rgba(255,255,255,0.16)" : "rgba(67,0,152,0.08)"} color={dark ? "white" : "primary"}>
            <Icon as={s.icon} boxSize="20px" />
          </Flex>
          <Text fontSize="12px" fontWeight="600" textAlign="center" lineHeight="1.3"
            color={dark ? "rgba(255,255,255,0.9)" : "gray.600"}>{s.label}</Text>
        </VStack>
        {i < steps.length - 1 && <Icon as={FiArrowRight} boxSize="16px" color={dark ? "rgba(255,255,255,0.5)" : "rgba(67,0,152,0.3)"} />}
      </React.Fragment>
    ))}
  </Flex>
);

const TrustModels: React.FC = () => (
  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: "18px", md: "22px" }}>
    <VStack align="flex-start" spacing="16px" bg="white" borderRadius="22px" p={{ base: "24px", md: "28px" }}
      border="1px solid" borderColor="rgba(67,0,152,0.1)" boxShadow="0 8px 24px rgba(67,0,152,0.06)">
      <Box>
        <Text fontSize="12px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" color="accent">Wygodny · dla większości</Text>
        <Heading fontSize="20px" fontWeight="800" color="primary" mt="2px">Model standardowy (token)</Heading>
      </Box>
      <ModelFlow steps={[
        { icon: FiMail, label: "Zaproszenie" },
        { icon: FiServer, label: "Platforma sprawdza listę" },
        { icon: FiCheckCircle, label: "Głos na łańcuch" }
      ]} />
      <Text fontSize="14px" color="gray.600" lineHeight="1.65">
        Platforma sprawdza uprawnienia (lista e-mail/token), a łańcuch przechowuje głos jako <b>niezaprzeczalny zapis</b>. Wygodne, bez instalacji — wystarczy link.
      </Text>
    </VStack>

    <VStack align="flex-start" spacing="16px" borderRadius="22px" p={{ base: "24px", md: "28px" }} color="white"
      bgGradient={`linear(150deg, ${PRIMARY} 0%, #6a1fc2 60%, #8b2fd6 100%)`} boxShadow="0 14px 34px rgba(67,0,152,0.22)">
      <Box>
        <Text fontSize="12px" fontWeight="800" letterSpacing="0.08em" textTransform="uppercase" color="#ff9bb0">Najmocniejszy · z portfelem</Text>
        <Heading fontSize="20px" fontWeight="800" mt="2px">Model suwerenny (portfel)</Heading>
      </Box>
      <ModelFlow dark steps={[
        { icon: FiKey, label: "Podpis Twoim kluczem" },
        { icon: FiShield, label: "Łańcuch egzekwuje" },
        { icon: FiCheckCircle, label: "Głos nie do podrobienia" }
      ]} />
      <Text fontSize="14px" color="rgba(255,255,255,0.92)" lineHeight="1.65">
        Ty podpisujesz głos <b>własnym kluczem tożsamości</b> (tylko na urządzeniu z portfelem). Łańcuch wymusza, że głosują wyłącznie uprawnione klucze — <b>nawet platforma nie podrobi Twojego głosu</b>. Wzmacniany weryfikacją tożsamości (mObywatel/Cyberspace).
      </Text>
    </VStack>
  </SimpleGrid>
);

/* ─── FAQ ──────────────────────────────────────────────────────────────── */
const FAQS = [
  { q: "Czy potrzebuję portfela, żeby głosować?", a: "Nie. Większość głosowań działa z samym zaproszeniem (token/e-mail) albo zalogowanym kontem. Portfel to mocniejsza, opcjonalna warstwa — wymagana tylko, gdy organizator ją włączy." },
  { q: "Czym jest „podpis cyfrową tożsamością\"?", a: "To podpisanie głosu kluczem, który masz wyłącznie Ty, w aplikacji na swoim urządzeniu (w bezpiecznym module sprzętowym). Dzięki temu nikt — nawet platforma — nie jest w stanie podrobić takiego głosu." },
  { q: "Dlaczego web nie obsługuje najwyższego poziomu?", a: "Bo klucz tożsamości żyje wyłącznie w bezpiecznym module Twojego urządzenia (aplikacja), a nie w przeglądarce. Jeśli głosowanie wymaga podpisu portfelem, dokończysz je w aplikacji." },
  { q: "Czy blockchain widzi, jak zagłosowałem?", a: "Przy głosowaniu tajnym — nie. Łańcuch przechowuje dowód uczestnictwa, a nie Twój wybór. Przy głosowaniu jawnym imiennym wybór jest świadomie powiązany z Tobą (jak imienne głosowanie w radzie)." },
  { q: "Co właściwie trafia na blockchain?", a: "Niezaprzeczalny, policzalny zapis: że głos padł, na którą opcję (w sposób zgodny z poziomem tajności) i że nie da się go zmienić. Każdy może niezależnie zweryfikować wynik w eksploratorze." }
];

export const VotingSecurityView: React.FC = () => {
  useEffect(() => {
    document.title = "Tożsamość i bezpieczeństwo głosu";
  }, []);

  return (
    <InfoPage
      eyebrow="Bezpieczeństwo i tożsamość"
      title="Jak chronimy Twój głos"
      subtitle="Od zaproszenia mailem po podpis cyfrową tożsamością — pokazujemy, kto może głosować, czym jest portfel i co gwarantuje blockchain."
    >
      <InfoBody>
        <InfoSectionTitle>Co blockchain gwarantuje zawsze</InfoSectionTitle>
        <Text fontSize="16px" color="gray.600" lineHeight="1.7" mb="28px">
          Niezależnie od ustawień głosowania, te trzy rzeczy są zawsze takie same. To fundament, na którym budujemy resztę.
        </Text>
        <GuaranteeCards />

        <Box mt={{ base: "52px", md: "72px" }}>
          <InfoSectionTitle>Drabina tożsamości wyborcy</InfoSectionTitle>
          <Text fontSize="16px" color="gray.600" lineHeight="1.7" mb="28px">
            Organizator decyduje, <b>jak mocna musi być tożsamość</b>, żeby oddać głos — od zwykłego zaproszenia po potwierdzoną tożsamość państwową.
          </Text>
          <EligibilityLadder />
        </Box>

        <Box mt={{ base: "52px", md: "72px" }}>
          <InfoSectionTitle>Web a urządzenie z portfelem</InfoSectionTitle>
          <Text fontSize="16px" color="gray.600" lineHeight="1.7" mb="28px">
            Klucz Twojej cyfrowej tożsamości (<b>@PL-U-</b>) żyje wyłącznie na urządzeniu z portfelem — w aplikacji. Przeglądarka go nie ma, więc najwyższy, suwerenny poziom dokończysz w aplikacji.
          </Text>
          <WebVsWallet />
          <Flex mt="20px" gap="12px" align="flex-start" bg="rgba(229,31,68,0.05)" border="1px solid" borderColor="rgba(229,31,68,0.15)" borderRadius="16px" p="18px">
            <Icon as={FiKey} color="accent" boxSize="20px" mt="2px" flexShrink={0} />
            <Text fontSize="15px" color="gray.700" lineHeight="1.6">
              <b>Podpis portfelem działa tylko z urządzenia z portfelem.</b> Web zawsze potwierdza tokenem — i to jest w porządku dla większości głosowań. Mocniejszy model włącza się wtedy, gdy naprawdę go potrzebujesz.
            </Text>
          </Flex>
        </Box>

        <Box mt={{ base: "52px", md: "72px" }}>
          <InfoSectionTitle>Dwa modele zaufania</InfoSectionTitle>
          <Text fontSize="16px" color="gray.600" lineHeight="1.7" mb="28px">
            Oba zapisują głos na łańcuchu. Różnią się tym, <b>kto pilnuje uprawnień</b> — i jak trudno podrobić głos.
          </Text>
          <TrustModels />
        </Box>

        <Box mt={{ base: "52px", md: "72px" }}>
          <InfoSectionTitle>Częste pytania</InfoSectionTitle>
          <FaqList items={FAQS} />
        </Box>
      </InfoBody>
    </InfoPage>
  );
};
